import {
  DOCS_CONTROLLING_PERSON,
  DOCS_FATCA_SUBSTANTIAL_US_OWNER,
  DOCS_UBO,
  WEB_DOCS,
  WebDocName,
} from '~/constants/docs.ts';
import { TDocument, UboOwnerMap } from './types';
import { WEB_FORMS_NAME } from '~/modules/identification/constants.ts';
import { TWebDocType } from '~/queries/types';
import {
  DATE_CHANGE_ADDITIONAL_VERIFICATION_APPLICATION,
  DOCUMENT_RELATED_ENTITY_TYPE,
} from '~/modules/identification/forms/Documents/constants.tsx';
import { TBuildUploadFormDataParams } from '~/store/identification-form/types.ts';

export const mapDocName = (
  isLegalResident: boolean,
  type?: { id: number; name: string },
  doc?: TDocument,
  blockId?: number
): string => {
  const isPersonalDoc = [
    WEB_DOCS[WebDocName.personalDataInformationAgreement],
    WEB_DOCS[WebDocName.foreignPassport],
    WEB_DOCS[WebDocName.taxpayerIdentificationNumber],
    WEB_DOCS[WebDocName.passport],
  ].includes(type?.id || -1);

  const shouldAddLabel =
    isLegalResident && isPersonalDoc && ((blockId && blockId === 2) || !blockId);

  if (!shouldAddLabel) {
    return type?.name || '';
  } else {
    return (
      type?.name + (doc && doc.inn ? ` (${doc.inn})` : '') + (doc && doc.pib ? ` - ${doc.pib}` : '')
    );
  }
};

export const getMessageValidBeforeSigning = (
  missingForms: string[],
  missingDocuments: number[],
  webDocs: TWebDocType[]
) => {
  const DOC_NAMES = webDocs.reduce(
    (acc, doc) => {
      acc[doc.id] = doc.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const forms = missingForms
    .filter((form) => form in WEB_FORMS_NAME)
    .map((form) => WEB_FORMS_NAME[form] as string);

  const documents = missingDocuments
    .filter((doc) => doc in DOC_NAMES)
    .map((doc) => DOC_NAMES[doc] as string);

  return { forms, documents };
};

export const getOwnerFullName = (owner: {
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
}) => [owner.lastName, owner.firstName, owner.middleName].filter(Boolean).join(' ');

export const checkDateAdditionalVerificationApplication = () => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  DATE_CHANGE_ADDITIONAL_VERIFICATION_APPLICATION.setHours(0, 0, 0, 0);

  return today >= DATE_CHANGE_ADDITIONAL_VERIFICATION_APPLICATION;
};

export const getFileInputId = (document: TDocument, blockId: number): string => {
  if (blockId === 3) return `fileInput-${blockId}-${document.id}`;

  const key = document.ownerId
    ? `${document.idDictWebDocument}-${document.ownerId}`
    : document.idDictWebDocument;

  return `fileInput-${blockId}-${key}`;
};

const STORAGE_KEY_PREFIX = 'ubo-doc-owner-map';

const getStorageKey = (applicationId: number | string) => `${STORAGE_KEY_PREFIX}-${applicationId}`;

export const getUboOwnerMap = (applicationId: number | string): UboOwnerMap => {
  try {
    const raw = localStorage.getItem(getStorageKey(applicationId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const setUboOwnerMapEntry = (
  applicationId: number | string,
  idDictWebDocument: number,
  idDoc: number,
  ownerId: string
) => {
  const map = getUboOwnerMap(applicationId);
  map[`${idDictWebDocument}:${idDoc}`] = ownerId;
  try {
    localStorage.setItem(getStorageKey(applicationId), JSON.stringify(map));
  } catch {}
};

export const getOwnerIdForDoc = (
  applicationId: number | string,
  idDictWebDocument: number,
  idDoc: number
): string | undefined => getUboOwnerMap(applicationId)[`${idDictWebDocument}:${idDoc}`];

export const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === 'null' ||
  value === 'undefined';

export const buildUploadFormData = ({
  requestData,
  appId,
  documentWebBlock,
  relatedEntityId,
  relatedEntityType,
  metadata,
}: TBuildUploadFormDataParams): FormData => {
  const formData = new FormData();
  formData.append('document', requestData.document as Blob);
  formData.append('docType', String(requestData.docType));
  formData.append('appId', String(appId));

  if (requestData.signature) {
    formData.append('signature', requestData.signature);
  }

  if (!isEmpty(documentWebBlock)) {
    formData.append('documentWebBlock', documentWebBlock as string);
  }

  if (!isEmpty(relatedEntityId) && !isEmpty(relatedEntityType)) {
    formData.append('relatedEntityId', relatedEntityId as string);
    formData.append('relatedEntityType', relatedEntityType as string);
  }

  if (metadata && Object.keys(metadata).length > 0) {
    formData.append('metadata', JSON.stringify(metadata));
  }

  return formData;
};

export const getRelatedEntityTypeByDoc = (
  idDictWebDocument: number
): DOCUMENT_RELATED_ENTITY_TYPE | undefined => {
  if (DOCS_FATCA_SUBSTANTIAL_US_OWNER.includes(idDictWebDocument)) {
    return DOCUMENT_RELATED_ENTITY_TYPE.FATCA_SUBSTANTIAL_US_OWNER;
  }
  if (DOCS_CONTROLLING_PERSON.includes(idDictWebDocument)) {
    return DOCUMENT_RELATED_ENTITY_TYPE.CONTROLLING_PERSON;
  }
  if (DOCS_UBO.includes(idDictWebDocument)) {
    return DOCUMENT_RELATED_ENTITY_TYPE.UBO;
  }
  return undefined;
};

export const normalizeId = (value?: string | null) => (value ?? '').trim().toLowerCase();
export const isSameId = (a?: string | null, b?: string | null) =>
  Boolean(a) && Boolean(b) && normalizeId(a) === normalizeId(b);
